import { useTranslation } from 'react-i18next'

import { Box, Divider, Stack, SxProps, Theme, Typography } from '@mui/material'

import responsiveHook from '@hooks/useResponsive'

import { Testimonial } from '@pages/LandingPage/components/Testimonial'

import { TLGTypography } from '@components/TLGTypography'

import { scaleUpTransition } from '@utils/styles'

import {
  adamTestimonialContainerSx,
  adamTestimonialTextSx,
  containerSx,
  contentGridSx,
  daveTestimonialContainerSx,
  daveTestimonialTextSx,
  dividerWrapperSx,
  findPerfectTechMatchLeftSideContentSx,
  findPerfectTechMatchLeftSideWrapperSx,
  findPerfectTechMatchRightSideImageSx,
  findPerfectTechMatchRightSideWrapperSx,
  larryTestimonialContainerSx,
  larryTestimonialTextSx,
  protectYourPrivacyAndControlLeftSideImageSx,
  protectYourPrivacyAndControlLeftSideWrapperSx,
  protectYourPrivacyAndControlRightSideContentSx,
  protectYourPrivacyAndControlRightSideWrapperSx,
  saveTimeAndStreamlineYourEvaluationProcessLeftSideContentSx,
  saveTimeAndStreamlineYourEvaluationProcessLeftSideWrapperSx,
  saveTimeAndStreamlineYourEvaluationProcessRightSideImageSx,
  saveTimeAndStreamlineYourEvaluationProcessRightSideWrapperSx,
  secondDividerWrapperSx,
  titleSx,
} from './styles'

const ContentSection = () => {
  const { t } = useTranslation()

  const { ifDesktop } = responsiveHook.useResponsive()

  return (
    <Box data-testid="home-content-section" sx={containerSx}>
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        sx={contentGridSx}
      >
        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={findPerfectTechMatchLeftSideWrapperSx}
        >
          <Stack sx={findPerfectTechMatchLeftSideContentSx}>
            <TLGTypography
              responsiveVariant={{ xs: 'h5', md: 'h4' }}
              sx={titleSx}
            >
              {t('Find your perfect tech match')}
            </TLGTypography>
            <Typography>
              {t(
                'Our platform empowers you to conduct a precise search for the technology, solutions, services, partners, and brands you need. Effortlessly refine your search based on budget, geography, preferred payment structures, and specific requirements. Say goodbye to sifting through irrelevant options and hello to a carefully curated selection of technology partners that align perfectly with your needs.'
              )}
            </Typography>

            <Testimonial
              testimonialText={t(
                'The Launchpad has been a great asset as we look to evaluate and purchase new technologies. They’ve done an awesome job matching technology partners with our needs.'
              )}
              personName="Dave C."
              personPosition={t('Senior Director, Information Technology')}
              containerSx={daveTestimonialContainerSx}
              textSx={daveTestimonialTextSx}
            />
          </Stack>
        </Box>

        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={findPerfectTechMatchRightSideWrapperSx}
        >
          <Box
            alt={t('Solutions Search Page')}
            loading="lazy"
            component="img"
            src="/images/solutions-search-page.webp"
            sx={
              {
                ...findPerfectTechMatchRightSideImageSx,
                ...ifDesktop(scaleUpTransition),
              } as SxProps<Theme>
            }
          />
        </Box>

        <Box gridColumn="span 12" sx={dividerWrapperSx}>
          <Divider />
        </Box>

        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={protectYourPrivacyAndControlLeftSideWrapperSx}
        >
          <Box
            alt={t('Solution Details Page')}
            loading="lazy"
            component="img"
            src="/images/protect-your-privacy-and-control.webp"
            sx={
              {
                ...protectYourPrivacyAndControlLeftSideImageSx,
                ...ifDesktop(scaleUpTransition),
              } as SxProps<Theme>
            }
          />
        </Box>

        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={protectYourPrivacyAndControlRightSideWrapperSx}
        >
          <Stack sx={protectYourPrivacyAndControlRightSideContentSx}>
            <TLGTypography
              responsiveVariant={{ xs: 'h5', md: 'h4' }}
              sx={titleSx}
            >
              {t('Protect your privacy and control')}
            </TLGTypography>
            <Typography>
              {t(
                "Protect your privacy and eliminate the flood of sales messages. Your profile stays hidden until you're ready to make contact with a potential partner. No more unsolicited sales calls or unwanted disruptions. You’re in control, deciding who sees your profile and when. Forge authentic connections based on your real requirements and experience a streamlined and efficient buyer-vendor relationship. "
              )}
            </Typography>
            <Testimonial
              testimonialText={t(
                'The vendor I connected to was very prepared and worked well with me on our call. They really focused on our pain points and briefly brought up what else the suite can do, rather than trying to take the whole time describing pieces we’re not ready for yet. It was well done. I would rate my experience as a 10 out of 10! This is the second time I’ve used The Launchpad, and both times you’ve exceeded my expectations. It removed a lot of that leg work I would have to do.'
              )}
              personName="Adam F."
              personPosition={t('Director of IT')}
              containerSx={adamTestimonialContainerSx}
              textSx={adamTestimonialTextSx}
            />
          </Stack>
        </Box>

        <Box gridColumn="span 12" sx={secondDividerWrapperSx}>
          <Divider />
        </Box>

        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={saveTimeAndStreamlineYourEvaluationProcessLeftSideWrapperSx}
        >
          <Stack
            sx={saveTimeAndStreamlineYourEvaluationProcessLeftSideContentSx}
          >
            <TLGTypography
              responsiveVariant={{ xs: 'h5', md: 'h4' }}
              sx={titleSx}
            >
              {t('Save time and streamline your evaluation process ')}
            </TLGTypography>
            <Typography>
              {t(
                "Time is your most valuable currency. Our platform eliminates the need for countless introductory calls and tedious research. Instead, you can efficiently review potential matches, initiate contact when you're ready, and streamline your evaluation process. Save time, energy, and your sanity while finding your perfect tech partner."
              )}
            </Typography>

            <Testimonial
              testimonialText={t(
                'It’s often a challenging and time-consuming effort to research prospective technology and security vendors when undertaking a new project or initiative. The Launchpad has helped streamline the vetting process and put me in front of several innovative up-and-coming security companies. With time an ever more valuable resource, I’m very pleased to have The Launchpad as a trusted platform to help me navigate the increasingly crowded security space.'
              )}
              personName="Larry K."
              personPosition={t('Global Head of Information Security')}
              containerSx={larryTestimonialContainerSx}
              textSx={larryTestimonialTextSx}
            />
          </Stack>
        </Box>

        <Box
          gridColumn={{ xs: 'span 12', lg: 'span 6' }}
          sx={saveTimeAndStreamlineYourEvaluationProcessRightSideWrapperSx}
        >
          <Box
            alt={t('Tech Vendor Profile and Saved Searches Pages')}
            loading="lazy"
            component="img"
            src="/images/saved-searches-and-tech-vendor-profile.webp"
            sx={{
              ...saveTimeAndStreamlineYourEvaluationProcessRightSideImageSx,
              ...ifDesktop(scaleUpTransition),
            }}
          />
        </Box>
      </Box>
    </Box>
  )
}

export { ContentSection }
